import { LoadingButton } from '@mui/lab';
import { Alert, Stack } from '@mui/material';
import { loginGuestUser } from 'api/auth';
import ConfigProvider from 'components/ConfigProvider';
import { RECAPTCHA_SITE_KEY } from 'constants';
import { AuthContext, SnackBarContext } from 'constants/contexts';
import useConfig from 'hooks/useConfig';
import useMerchant from 'hooks/useMerchant';
import useSentryUpdateEffect from 'hooks/useSentryUpdateEffect';
import React, { useContext, useEffect, useState } from 'react';
import ReCaptchaV2 from 'react-google-recaptcha';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { useHistory } from 'react-router';
import { getQueryStringValue } from 'utils';
import { captureException } from 'utils/error';
import { SessionJsonStorage } from 'utils/storage';
import AuthWrapper from './AuthWrapper';

function Login() {
  const quotedOrder = SessionJsonStorage.get('quoted-order') || {};
  const [phone, setPhone] = useState(quotedOrder.phone);
  const [countryDialCode, setCountryDialCode] = useState('1');
  const [phoneIsValid, setPhoneIsValid] = useState(false);
  const [loading, setLoading] = useState(false);
  const [reCaptchaToken, setReCaptchaToken] = useState();
  const history = useHistory();
  const config = useConfig();
  const { user, setUser } = useContext(AuthContext);
  const showSnackBar = useContext(SnackBarContext);
  const { merchant, loading: merchantLoading } = useMerchant();

  useSentryUpdateEffect({
    page: 'login',
    phone,
  }, [phone]);

  useEffect(() => {
    if (user?.phone && user?.phone?.[0] === '+') {
      setPhone(user?.phone);
    }
  }, [user?.phone]);

  const handleChangePhone = (value) => {
    if (!value) {
      setPhone('');
    } else {
      setPhone(`+${value}`);
    }
  };

  const goToOTPPage = () => {
    if (phoneIsValid) {
      history.replace('/otp');
    }
  };

  const handleSubmit = () => {
    setLoading(true);
    const rewardEligibilityToken = getQueryStringValue('reward_eligibility_token');
    loginGuestUser({
      phone, organization: ConfigProvider.ORG_ID, captcha: reCaptchaToken, rewardEligibilityToken,
    })
      .then((res) => {
        setUser({
          customer: res?.customer,
          token: res?.token,
          tokenVerified: false,
          phone,
          new_registration: res?.new_registration,
        });
        setLoading(false);
        /** If it's the login page go to OTP page */
        goToOTPPage();
      })
      .catch((err) => {
        /** If OTP has been sent before go to OTP page again */
        if (
          err.response?.status === 422 &&
          err.response?.data?.title === 'CodeAlreadySent'
        ) {
          goToOTPPage();
          showSnackBar({ message: err.response.data.errors.phone, type: 'warning', autoHideDuration: 6000 });
          return;
        }
        captureException(err);
        setLoading(false);
        showSnackBar({ message: 'Something went wrong!', type: 'error' });
      });
  };

  return (
    <AuthWrapper
      subTitle={`By entering your phone number you authorize ${merchant?.name || config?.general?.name} to send text messages with OTP. Message/data rates apply.`}
      action={(
        <LoadingButton
          variant="muted"
          fullWidth
          disabled={!phone || (process.env.REACT_APP_NODE_ENV === 'production' && !reCaptchaToken) || !phoneIsValid}
          loading={loading}
          onClick={handleSubmit}

        >
          Get code
        </LoadingButton>
      )}
      alert={quotedOrder?.phone && (
        <Alert severity="info" icon={false}>
          Quoted order assigned to
          {' '}
          <strong>{quotedOrder.phone}</strong>
          , expires at
          {' '}
          {quotedOrder.dueDate}
          .
          {' '}
          Make sure you&#39;ve entered this phone number when you request the code. You&#39;ll receive the OTP both as a text message to your
          {' '}
          <strong>phone</strong>
          {' '}
          and also sent to your
          {' '}
          <strong>email</strong>
          . Log in with this number to view the order.
        </Alert>
      )}
      loading={merchantLoading}
    >

      <PhoneInput
        country={config?.general?.defaultAreaCode || 'ca'}
        buttonStyle={{
          borderRadius: '12px 0 0 12px',
          padding: '0 4px',
          background: 'none',
          borderWidth: '1px 0',
        }}
        inputStyle={{
          borderRadius: 12, height: 48, paddingLeft: '56px', width: '100%',
        }}
        value={phone}
        onChange={(value, country) => {
          handleChangePhone(value);
          setCountryDialCode(country?.dialCode);
        }}
        placeholder="Enter phone number"
        autofocus
        preferredCountries={['us', 'ca']}
        enableSearch
        isValid={(value, country) => {
          setPhoneIsValid(false);

          if (!country?.dialCode) {
            return 'Please select a country';
          }
          if (phone?.length && phone?.length <= countryDialCode?.length) {
            return 'Please enter phone number';
          }
          setPhoneIsValid(true);
          return true;
        }}
        regions={['america', 'europe']}
        countryCodeEditable={false}
        masks={{
          pr: '(...) ...-....',
        }}
        disabled={loading}
      />

      {process.env.REACT_APP_NODE_ENV === 'production' && (
        <Stack
          alignItems="start"
          width="100%"
          sx={{
            '& .recaptcha': {
              '& > div': {
                borderRadius: '12px',
                overflow: 'hidden',
                border: '1px solid #d3d3d3',
                width: '302px',
                height: '76px',
              },
            },
            '& iframe': {
              marginLeft: '-1px',
              marginTop: '-1px',
            },
          }}
        >
          <ReCaptchaV2
            sitekey={RECAPTCHA_SITE_KEY}
            className="recaptcha"
            onChange={setReCaptchaToken}
            onExpire={() => setReCaptchaToken(null)}
          />
        </Stack>
      )}

    </AuthWrapper>
  );
}

export default Login;
